/**
 * @component BatchTransactionComponent
 * @description Manages batch feed transaction entry for aquaculture ponds. This component handles multi-pond feed logging
 * with support for feed types, feed mixes, and quantity tracking. It provides auto-fill capabilities from previous batch entries,
 * validation of UOM conversions, and transaction processing with check tray readings.
 *
 * ## Key Features:
 * 1. **Multi-Pond Feed Entry** - Add feed logs for multiple ponds within a single batch transaction
 * 2. **Auto-Fill from Previous Batches** - Automatically populate feeds from the latest batch with date-based proximity filtering
 * 3. **Feed Type & Mix Management** - Support for primary feed types and optional feed mixes with flexible UOM conversions
 * 4. **Check Tray Readings** - Track check tray feed given and consumed for quality validation
 * 5. **Dynamic UOM Conversion** - Intelligent UOM selection based on feed item type and conversion rules
 * 6. **Meal Entry Management** - Add/remove meal entries per pond with validation
 * 7. **Form State Management** - Preview, edit, and submission workflow with dynamic field editability
 * 8. **Batch-Level Operations** - Apply meal data to all ponds, cumulative feed calculation by date
 * 9. **Transaction Processing** - UOM conversion validation before batch entry creation
 * 10. **Data Persistence** - Create batch transactions and feed log transactions with proper object serialization
 *
 * ## Architecture:
 * The component operates through multiple hierarchical levels:
 * - **Pond Level**: Each pond has culture data and a set of feed logs
 * - **Meal Level**: Each feed log represents a meal entry with feed type, quantity, and optional mix
 * - **Feed Type Level**: Each meal contains feed type details with UOM conversions
 * - **Check Tray Level**: Validates feed consumption within each meal
 *
 * ## Role-Based Behavior:
 * - **Business Admin Login**: Can access all farm sites and sections
 * - **Farmer Login**: Automatically filtered to their assigned business entity
 * - Business entity provider relationships are respected for multi-provider scenarios
 *
 * ## Data Flow:
 * 1. Load business entity and farm hierarchy (business → farm sites → sections → ponds)
 * 2. Fetch feed types and feed mixes for the selected business
 * 3. Get latest batch for potential auto-fill
 * 4. User enters meal data with validation at each step
 * 5. Preview validation ensures all required fields are populated
 * 6. UOM conversion check validates feed quantity conversions
 * 7. Batch transaction and transaction records are created simultaneously
 *
 * ## Form States:
 * - **Entry Mode**: All fields editable, preview button visible
 * - **Preview Mode**: Fields locked, edit and submit buttons visible
 * - **Submitted**: Transaction created, state reset to entry mode
 *
 * @selector app-batch-transaction
 * @implements {OnInit, OnDestroy}
 * @lifecycle ngOnInit - Initialize form, load dependencies, fetch latest batch data
 * @lifecycle ngOnDestroy - Cancel HTTP requests and cleanup subscriptions
 *
 * ## Dependencies (10 services):
 * @service ToastrService - Display toast notifications for user feedback
 * @service TransactionsService - Create and manage transaction records
 * @service ItemMasterService - Fetch feed types, mixes, and UOM conversion data
 * @service FeedLogService - Retrieve and validate feed log records
 * @service CustomerPondService - Load pond and culture data by section
 * @service PondSectionService - Fetch sections by farm site
 * @service BusinessEntityService - Business hierarchy and provider relationships
 * @service FarmSiteService - Farm site data by business entity
 * @service BatchTransactionsService - Create batch feed transactions
 * @service CommonMethodsService - Shared utility functions
 * @service Sidenavaction - Navigation state management (injected but not actively used)
 *
 * @example
 * // Component initialization flow:
 * // 1. Constructor initializes empty model objects
 * // 2. ngOnInit loads login context and initializes date picker
 * // 3. User selects business → Loads farm sites
 * // 4. User selects farm site → Loads sections
 * // 5. User selects section → Loads ponds and prepares meal entries
 * // 6. User enters meal data → Validates at each step
 * // 7. User clicks preview → Locks form for review
 * // 8. User clicks submit → Validates UOM and creates transactions
 *
 * @version 1.0 - Initial implementation with multi-pond batch entry
 */

import {Component, EventEmitter, Input, OnInit, Output, OnDestroy} from '@angular/core';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import {BatchTransactionsService} from 'src/app/services/batchTransactions.service';
import BatchTransactionsModel from 'src/app/services/batchTransactions.model';
import {PondSectionService} from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {FeedLogService} from 'src/app/services/feedLog.service';
import FeedLogModel, {ItemUOM, MixUOM} from 'src/app/services/feedLog.model';
import {ItemMasterService} from 'src/app/services/itemMaster.service';
import ItemMasterModel from 'src/app/services/itemMaster.model';
import UOMMasterModel from 'src/app/services/uom.model';
import FeedMealModel from 'src/app/services/feedMeal.model';
import CheckTrayReadingModel from 'src/app/services/checkTaryReading.model';
import {TransactionsService} from 'src/app/services/transactions.service';
import TransactionsModel from 'src/app/services/transactions.model';
import FeedTypeModel from 'src/app/services/feedType.model';
import * as moment from 'moment';
import * as _ from 'lodash';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';

declare var $: any;

@Component({
  selector: 'app-batch-transaction',
  templateUrl: './batch-transaction.component.html',
  styleUrls: ['./batch-transaction.component.scss']
})
export class BatchTransactionComponent implements OnInit, OnDestroy {
  /**
   * @type {Array<BusinessEntity>}
   * @description Array of available business entities for selection in dropdown
   * @default undefined - Populated on component initialization or role-based loading
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * @type {Array<FarmSiteModel>}
   * @description List of farm sites filtered by selected business entity
   * @default undefined - Populated when business entity is selected
   * @side-effect Triggers section loading when a farm site is selected
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * @type {Array<BatchTransactionsModel>}
   * @description Collection of batch transaction records for a specific batch
   * @default undefined - Populated from backend API
   */
  public batchTransactions: Array<BatchTransactionsModel>;

  /**
   * @type {BatchTransactionsModel}
   * @description Current batch transaction being created with all feed logs and metadata
   * @default new BatchTransactionsModel() - Initialized in constructor
   * @side-effect Contains the complete transaction payload sent to backend
   */
  public batchTransaction: BatchTransactionsModel;

  /**
   * @type {Array<PondSectionModel>}
   * @description List of pond sections filtered by selected farm site
   * @default undefined - Populated when farm site is selected
   * @side-effect Triggers pond loading when a section is selected
   */
  public pondSections: Array<PondSectionModel>;

  /**
   * @type {Array<CustomerPondModel>}
   * @description All ponds within selected section (includes preparatory ponds)
   * @default undefined - Populated when section is selected
   * @side-effect Used internally to build pondsDetails_dropdown with culture data
   */
  public customerPonds: Array<CustomerPondModel>;

  /**
   * @type {TransactionsModel}
   * @description Represents a single transaction record created during batch submission
   * @default undefined - Populated on successful batch submission
   */
  public transaction: TransactionsModel;

  /**
   * @type {Array<FeedLogModel>}
   * @description Array of feed log records retrieved for cumulative feed calculation
   * @default undefined - Populated when calculating cumulative feed by date
   */
  public feedLogs: Array<FeedLogModel>;

  /**
   * @type {FeedLogModel}
   * @description Current feed log object being constructed for meal entry
   * @default new FeedLogModel() - Initialized in constructor
   * @side-effect Pushed to batchTransaction.FeedLogs when meal entry is added
   */
  public feedLog: FeedLogModel;

  /**
   * @type {FeedMealModel}
   * @description Meal details including feed type, quantity, and consumption data
   * @default undefined - Instantiated when creating feed log entries
   */
  public feedMeal: FeedMealModel;

  /**
   * @type {CheckTrayReadingModel}
   * @description Check tray reading data (feed given, consumed, and CTIR flag)
   * @default undefined - Instantiated within feed log creation
   */
  public checkTray: CheckTrayReadingModel;

  /**
   * @type {Array<ItemMasterModel>}
   * @description List of available feed types/mixes from item master
   * @default undefined - Populated during business-level initialization
   */
  public itemMasters: Array<ItemMasterModel>;

  /**
   * @type {ItemMasterModel}
   * @description Current item master being processed for UOM conversion validation
   * @default new ItemMasterModel() - Initialized during checkUOM operation
   */
  public itemMaster: ItemMasterModel;

  /**
   * @type {Array<UOMMasterModel>}
   * @description Unit of measure master records
   * @default undefined - Available but not actively used (UOMs loaded via conversions)
   */
  public uoms: Array<UOMMasterModel>;

  /**
   * @type {UOMMasterModel}
   * @description Single unit of measure record
   * @default undefined - Used during UOM lookup operations
   */
  public uom: UOMMasterModel;

  /**
   * @type {FeedTypeModel}
   * @description Current feed type being processed for transaction validation
   * @default new FeedTypeModel() - Initialized during checkUOM operation
   * @side-effect Pushed to itemMaster.feedTypes for validation
   */
  public feedType: FeedTypeModel;

  /**
   * @type {ItemUOM}
   * @description Feed item's unit of measure details with conversion factors
   * @default undefined - Populated from feed type UOM conversion data
   */
  public itemUom: ItemUOM;

  /**
   * @type {MixUOM}
   * @description Feed mix's unit of measure details with conversion factors
   * @default undefined - Populated from feed mix UOM conversion data
   */
  public mixUom: MixUOM;

  /**
   * @type {boolean}
   * @description Page loader state for initial data fetching
   * @default false - Set to true during async operations
   * @side-effect Shows spinner during business/section/pond loading
   */
  pgLoad = false;

  /**
   * @type {string}
   * @description Login person type from localStorage (e.g., 'EMPLOYEE', 'Business Admin', 'Provider Manager')
   * @default undefined - Retrieved from localStorage on ngOnInit
   * @side-effect Determines data loading logic (all vs business-specific)
   */
  loginPersonType: string;

  /**
   * @type {string}
   * @description Name of logged-in user
   * @default undefined - Retrieved from localStorage
   */
  LoginUserName: string;

  /**
   * @type {string}
   * @description Business name of logged-in user for transaction records
   * @default undefined - Retrieved from localStorage or computed from selected business
   */
  loginUserBusinessName: string;

  /**
   * @type {boolean}
   * @description Response loader state for data fetching operations
   * @default false - Set to true when section/pond data is being loaded
   * @side-effect Controls visibility of loading indicators
   */
  public responseLoader: boolean;

  /**
   * @type {Array<any>}
   * @description Dropdown data for ponds with culture details, feed logs, and auto-fill status
   * @default [] - Empty array initialized, populated when section is selected
   * @side-effect Main data structure for UI rendering of pond cards and meal entries
   * @structure Each item contains: { id, pond_id, pond_name, start_date, stocking_count, present_no_of_animals,
   *           culture_category_id, PondCultureData, FeedLogs[], Autofill: boolean }
   */
  public pondsDetails_dropdown = new Array();

  /**
   * @type {Date}
   * @description Batch entry date selected via date picker
   * @default undefined - Set via initializedatePicker function
   * @side-effect Used for DOC (Days of Culture) calculation and cumulative feed lookup
   */
  public batchEntrtyDate: any;

  /**
   * @type {Array<FeedTypeModel>}
   * @description Cached list of feed types for the selected business
   * @default [] - Populated by getFeedTypes()
   * @side-effect Used for dropdown and UOM lookup operations
   */
  public feedTypes = new Array();

  /**
   * @type {Array<ItemMasterModel>}
   * @description Cached list of feed mixes for the selected business
   * @default [] - Populated by getFeedMix()
   * @side-effect Used for mix dropdown and mix UOM lookup
   */
  public feedMix = new Array();

  /**
   * @type {Date}
   * @description Current date/time captured at component initialization
   * @default new Date() - Set in ngOnInit
   * @side-effect Used for batch transaction timestamp
   */
  public date_time: any;

  /**
   * @type {string}
   * @description Current time formatted as HH:mm (e.g., "02:30 PM")
   * @default undefined - Set in ngOnInit using moment.js
   * @side-effect Applied to meal entries for given_time field
   */
  public given_time: string;

  /**
   * @type {boolean}
   * @description Disable state for commit/submit button during transaction processing
   * @default false - Set to true during UOM check and submission
   * @side-effect Prevents duplicate submissions while async operations are pending
   */
  public isCommitDisabled: boolean;

  /**
   * @type {boolean}
   * @description Disable state for download button during transaction processing
   * @default false - Set to true during submission or on error
   * @side-effect Prevents download actions during pending operations
   */
  public disableDownload: boolean;

  /**
   * @type {EventEmitter<boolean>}
   * @description Output event emitted when batch transaction is successfully created
   * @default new EventEmitter() - Emits true on successful submission
   * @side-effect Parent component navigates back to batch list on true emission
   */
  @Output() gotoBatchList: EventEmitter<any> = new EventEmitter<any>();

  /**
   * @type {boolean}
   * @description Flag to show/hide meal entry table
   * @default false - Set to true when ponds are selected
   * @side-effect Controls visibility of feed log entry UI
   */
  public mealEntryTable: boolean;

  /**
   * @type {boolean}
   * @description Controls editability of meal text in preview mode
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Disables text input for meal entries in preview state
   */
  public noMealEditText = true;

  /**
   * @type {boolean}
   * @description Controls visibility/editability of add meal icon
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Hides add button when form is in preview mode
   */
  public noAddIconEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of last meal checkbox
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents modification of meal sequencing in preview mode
   */
  public lastMealEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of feed type dropdown
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents feed type changes after form preview
   */
  public noFeedTypeEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of feed quantity input
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents quantity modification in preview state
   */
  public nogeedQtyEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of skip reason textarea
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Only enabled when skip reason feed type (ID 99) is selected
   */
  public noreasonEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of feed UOM dropdown
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents UOM changes after form validation
   */
  public noUomEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of mix UOM dropdown
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents mix UOM modification in preview mode
   */
  public noMixUomEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of feed mix type dropdown
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Disables mix selection changes after preview
   */
  public noFeedMixTypeEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of mix quantity input
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents mix quantity modification in preview state
   */
  public noMixQtyEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of check tray feed given input
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents check tray given modification in locked state
   */
  public nocheckFeedGivenEditable = true;

  /**
   * @type {boolean}
   * @description Controls editability of check tray feed consumed input
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Prevents check tray consumed modification in locked state
   */
  public nocheckFeedConsumedEditable = true;

  /**
   * @type {boolean}
   * @description Controls visibility/editability of delete meal row icon
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Hides delete button when form is in preview mode
   */
  public noMealDeleteIconEditable = true;

  /**
   * @type {boolean}
   * @description Controls visibility/editability of "apply to all ponds" button
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Disables bulk copy operation in preview state
   */
  public noApplyAllPondsEditable = true;

  /**
   * @type {boolean}
   * @description Controls visibility/editability of delete pond card icon
   * @default true - Locked during preview, unlocked during edit
   * @side-effect Hides pond card delete button when form is locked
   */
  public noCardDeleteEditable = true;

  /**
   * @type {boolean}
   * @description Visibility state of edit button
   * @default false - Shown only in preview mode
   * @side-effect Allows user to return to edit mode from preview
   */
  public editButton = false;

  /**
   * @type {boolean}
   * @description Visibility state of submit button
   * @default false - Shown only in preview mode
   * @side-effect Triggers batch transaction creation and validation
   */
  public submitButton = false;

  /**
   * @type {boolean}
   * @description Visibility state of preview button
   * @default true - Shown in edit mode
   * @side-effect Triggers form validation and transitions to preview mode
   */
  public previewButton = true;

  /**
   * @type {any}
   * @description Latest feed log records from previous batch (used for auto-fill)
   * @default undefined - Populated from API response
   */
  public LatestFeedLogs: any;

  /**
   * @type {boolean}
   * @description Checkbox state for last meal indicator
   * @default false - Manually checked by user
   * @side-effect Marks meal as final in sequence
   */
  public isChecked: boolean;

  /**
   * @type {string}
   * @description Input parameter: Business entity ID passed from parent component
   * @default undefined - Set via @Input binding
   * @side-effect Filters farm sites and other dropdowns
   */
  @Input('businessId') paramsbusinessId: string;

  /**
   * @type {string}
   * @description Input parameter: Farm site ID passed from parent component
   * @default undefined - Set via @Input binding
   * @side-effect Filters sections when provided on init
   */
  @Input('siteId') paramsfarmsiteId: string;

  /**
   * @type {string}
   * @description Input parameter: Pond section ID passed from parent component
   * @default undefined - Set via @Input binding
   * @side-effect Auto-loads ponds when provided on init
   */
  @Input('sectionId') paramssectionId: string;

  /**
   * @type {string}
   * @description Input parameter: Business name passed from parent component
   * @default undefined - Set via @Input binding
   * @side-effect Used in transaction records metadata
   */
  @Input('businessName') businessName: string;

  /**
   * @type {string}
   * @description Current date formatted as string for display
   * @default undefined - Set from date_time in ngOnInit
   */
  public currentDate: string;

  /**
   * @type {boolean}
   * @description Exit value flag for meal entry validation
   * @default undefined - Set during preview button validation
   * @side-effect Indicates if at least one complete meal was entered
   */
  public valueExit: boolean;

  /**
   * @type {BusinessEntity}
   * @description Business entity object parsed from localStorage
   * @default undefined - Retrieved and parsed during getFeedTypes/getFeedMix
   * @side-effect Determines business hierarchy for provider relationships
   */
  public businessEntity: BusinessEntity;

  /**
   * @type {any}
   * @description Transaction request object for UOM validation
   * @default undefined - Built during checkUOM operation
   * @side-effect Contains farm site ID, dates, and feed transactions for validation
   */
  private newTransactionReq: any;

  /**
   * @type {any}
   * @description Latest batch feed log details from previous batch
   * @default undefined - Used for auto-fill reference data
   */
  public latestBatchFeedlogDetails: any;

  /**
   * @type {Array<BatchTransactionsModel>}
   * @description Latest batch transaction records for the section
   * @default undefined - Fetched in ngOnInit for auto-fill
   * @side-effect Used to retrieve previous meal entries for population
   */
  public latestBatchTransaction: Array<BatchTransactionsModel>;

  /**
   * @type {Date}
   * @description Maximum date allowed in date picker
   * @default new Date() - Current date (prevents future date selection)
   * @side-effect Applied to jQuery date picker settings
   */
  maxDate: Date = new Date();

  /**
   * @method constructor
   * @description Initialize component with service dependencies and model instances
   *
   * @param {ToastrService} toasterService - @service Toast notification service for user feedback
   * @param {TransactionsService} transactionService - @service Create and manage transaction records
   * @param {ItemMasterService} itemMastersService - @service Fetch feed types, mixes, and UOM conversion data
   * @param {FeedLogService} feedLogService - @service Retrieve feed log records and validate entries
   * @param {CustomerPondService} customerPondService - @service Load pond and culture data by section
   * @param {PondSectionService} pondSectionService - @service Fetch pond sections by farm site ID
   * @param {BusinessEntityService} businessEntityService - @service Business hierarchy and provider relationships
   * @param {FarmSiteService} farmSitesService - @service Retrieve farm site data by business entity
   * @param {BatchTransactionsService} batchTransactionService - @service Create batch feed transactions
   * @param {Sidenavaction} sidenavaction - @service Navigation state management
   * @param {CommonMethodsService} commonMethodsService - @service Shared utility methods and error handling
   *
   * @description Service initialization details:
   * - ToastrService: Displays error, success, and warning notifications
   * - TransactionsService: Manages transaction persistence with UOM validation
   * - ItemMasterService: Loads feed types/mixes with UOM conversion rules
   * - FeedLogService: Handles feed log CRUD operations and cumulative calculations
   * - CustomerPondService: Provides pond and culture hierarchy data
   * - PondSectionService: Retrieves sections for farm sites
   * - BusinessEntityService: Resolves provider relationships and business hierarchy
   * - FarmSiteService: Loads farm site options
   * - BatchTransactionsService: Creates batch-level transactions
   * - Sidenavaction: Navigation side effects
   * - CommonMethodsService: Common utilities for error handling and user notifications
   *
   * @side-effect Initializes model instances:
   * - batchTransaction = new BatchTransactionsModel()
   * - feedLog = new FeedLogModel()
   * - itemMaster = new ItemMasterModel()
   * - feedType = new FeedTypeModel()
   * - feedLogs = new Array()
   */
  constructor(private toasterService: ToastrService, public transactionService: TransactionsService, public itemMastersService: ItemMasterService, public feedLogService: FeedLogService, public customerPondService: CustomerPondService, public pondSectionService: PondSectionService, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public batchTransactionService: BatchTransactionsService, public sidenavaction: Sidenavaction,
    public commonMethodsService: CommonMethodsService) {
    this.batchTransaction = new BatchTransactionsModel();
    this.feedLog = new FeedLogModel();
    this.itemMaster = new ItemMasterModel();
    this.feedType = new FeedTypeModel();
    this.feedLogs = new Array();
  }

  /**
   * @method ngOnInit
   * @async
   * @lifecycle Angular lifecycle hook - called after component initialization
   * @description Initialize component state, load login context, and prepare initial data
   *
   * Execution flow:
   * 1. Reset response loader and fetch login person type from localStorage
   * 2. Capture current date/time for batch transaction timestamp
   * 3. Format current time as "hh:mm A" (e.g., "02:30 PM")
   * 4. Initialize jQuery date picker for batch entry date selection
   * 5. Set meal entry table and validation flags to initial state
   * 6. Fetch latest batch transaction for potential auto-fill
   * 7. Load feed types and feed mixes if business ID is provided
   * 8. Load ponds if section ID is provided (from @Input parameters)
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Sets: responseLoader = false, loginPersonType, date_time, given_time
   * - Sets: mealEntryTable = false, isChecked = false, disableDownload = false, isCommitDisabled = false
   * - Fetches: latestBatchTransaction, feedTypes, feedMix, pondsDetails_dropdown
   * - Calls: initializedatePicker(), getFeedTypes(), getFeedMix(), getPondsBySectionId()
   */
  public async ngOnInit() {
    try {
      this.responseLoader = false;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.date_time = new Date();
      this.given_time = moment(new Date()).format('hh:mm A');
      this.initializedatePicker(this);
      this.mealEntryTable = false;
      this.isChecked = false;
      this.disableDownload = false;
      this.isCommitDisabled = false;
      this.latestBatchTransaction = await this.batchTransactionService.getLatestFeedBatchTransaction(this.paramsbusinessId, this.paramsfarmsiteId, Number(this.paramssectionId));
      if (this.paramsbusinessId) {
        await this.getFeedTypes();
        await this.getFeedMix();
      }
      if (this.paramssectionId) {
        this.getPondsBySectionId(this.paramssectionId);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle Angular lifecycle hook - called before component is destroyed
   * @description Cancel ongoing HTTP requests to prevent memory leaks and subscription issues
   *
   * Cleanup operations:
   * - Cancels all pending HTTP requests in BusinessEntityService
   * - Cancels all pending HTTP requests in ItemMasterService
   * - Cancels all pending HTTP requests in CustomerPondService
   *
   * @returns {void}
   * @side-effect Aborts in-flight HTTP requests through service cancellation tokens
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.itemMastersService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Fetch all farm sites for the selected business entity (admin login)
   *
   * Execution flow:
   * 1. Clear previously selected farm site ID
   * 2. Call FarmSiteService to retrieve all farm sites by business ID
   * 3. Set response loader to true
   * 4. If only one farm site exists, auto-select it
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Clears: paramsfarmsiteId = ''
   * - Populates: farmSites array
   * - Sets: responseLoader = true
   * - May trigger: getSetionsByFarmsiteId() if single farm site exists
   */
  public async getAllFarmsitesBybusinessId() {
    try {
      this.paramsfarmsiteId = '';
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.paramsbusinessId);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsitesByFarmerLoginBusinessID
   * @async
   * @description Fetch all farm sites for the logged-in farmer's business ID
   *
   * Execution flow:
   * 1. Clear previously selected farm site ID
   * 2. Call FarmSiteService with farmer's business ID from localStorage
   * 3. Set response loader to true
   * 4. If only one farm site exists, auto-select it
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Clears: paramsfarmsiteId = ''
   * - Populates: farmSites array
   * - Sets: responseLoader = true
   * - Reads: localStorage.businessID (farmer's business ID)
   * - May trigger: getSetionsByFarmsiteId() if single farm site exists
   */
  public async getAllFarmsitesByFarmerLoginBusinessID() {
    try {
      this.paramsfarmsiteId = '';
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(localStorage.businessID);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-select farm site if only one exists in the list
   *
   * Logic: If farmSites array has exactly one item, automatically populate paramsfarmsiteId
   * and trigger section loading for better UX (avoids extra user clicks)
   *
   * @returns {void}
   * @side-effect
   * - Sets: paramsfarmsiteId (if single farm site exists)
   * - May trigger: getSetionsByFarmsiteId() for automatic progression
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.paramsfarmsiteId = this.farmSites[0].id; // model-binding key
      this.getSetionsByFarmsiteId(this.paramsfarmsiteId);
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Fetch all pond sections for the selected farm site
   *
   * Execution flow:
   * 1. Clear previously selected section ID
   * 2. Call PondSectionService to retrieve sections by farm site ID
   * 3. If only one section exists, auto-select it
   *
   * @param {string} farmsiteid - The farm site ID to fetch sections for
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Clears: paramssectionId = undefined
   * - Populates: pondSections array
   * - May trigger: getPondsBySectionId() if single section exists
   */
  public async getSetionsByFarmsiteId(farmsiteid) {
    try {
      this.paramssectionId = undefined;
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteid);
      this.setSingleValueForSection();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForSection
   * @description Auto-select pond section if only one exists in the list
   *
   * Logic: If pondSections array has exactly one item, automatically populate paramssectionId
   * and trigger pond loading for better UX (avoids extra user clicks)
   *
   * @returns {void}
   * @side-effect
   * - Sets: paramssectionId (if single section exists)
   * - May trigger: getPondsBySectionId() for automatic progression
   */
  public setSingleValueForSection() {
    if (this.pondSections && this.pondSections.length == 1) {
      this.paramssectionId = this.pondSections[0].id; // model-binding key
      this.getPondsBySectionId(this.paramssectionId);
    }
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Load all ponds for selected section and prepare meal entry structure with auto-fill capability
   *
   * Execution flow:
   * 1. Reset response loader and clear pondsDetails_dropdown array
   * 2. Fetch feed log data from latest batch transaction (if exists)
   * 3. Fetch all ponds by section ID from database
   * 4. Filter and structure ponds: Only include non-preparatory ponds with active PondCultureData
   * 5. Build pondsDetails_dropdown with pond and culture information
   * 6. Check if auto-fill from previous batch is desired (via confirm dialog)
   * 7. If yes:
   *    - For each pond, find closest matching feed log (by pond_id and nearest date)
   *    - Populate feed meal array with feed types, UOM conversions, and check tray data
   *    - Calculate closest meal by time difference
   * 8. If no or no previous data:
   *    - Initialize empty meal entry for each pond
   *    - Set Autofill = false flag
   *
   * @param {string} section_id - The pond section ID to fetch ponds for
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Sets: responseLoader = false, then true after data load
   * - Populates: customerPonds, pondsDetails_dropdown array
   * - Populates: Each pond item contains FeedLogs array with initialized FeedLogModel objects
   * - Each FeedLogModel contains: feedMeal (with feedTypes, skip_reason), checkTray, itemUom, mixUom
   * - Sets: Autofill flag for each pond
   * - May populate: batchTransaction.FeedLogs with feed entries
   *
   * @description Auto-fill Logic:
   * For each pond in the latest batch feed logs:
   * 1. Calculate time difference between each meal's last_feed_given_date and current date
   * 2. Find the meal with minimum time difference (closest match)
   * 3. Copy that meal data to current batch entry
   * 4. Replicate UOM conversion structure for consistency
   *
   * @description Meal Entry Initialization:
   * When creating blank meal entries:
   * - feedMeal.feedTypes: Array with single object containing -1 IDs (unselected state)
   * - feedMeal.skip_reason: null (for optional skip reason when feed type is 99)
   * - checkTray: CTIR = false, imagecount = 0, readings = null
   * - itemUom: [] (populated when feed type is selected)
   * - mixUom: [] (populated when feed mix is selected)
   */
  public async getPondsBySectionId(section_id: string) {
    this.responseLoader = false;
    this.pondsDetails_dropdown = new Array();
    let feedLogData: any[];
    try {
      if (this.latestBatchTransaction && this.latestBatchTransaction.length > 0) {
        feedLogData = await this.feedLogService.getFeedLogsByBatchNumber(this.latestBatchTransaction[0].id);
      }
      this.customerPonds = await this.customerPondService.getAllPondsBySectionId(section_id);
      this.pondsDetails_dropdown = new Array();
      this.responseLoader = true;
      if (this.customerPonds && this.customerPonds.length > 0) {
        for (let i = 0; i < this.customerPonds.length; i++) {
          let eachrec = this.customerPonds[i];
          if (eachrec.isPreparatory == false && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
            for (let j = 0; j < eachrec.PondCultureData.length; j++) {
              let eachpondculture = eachrec.PondCultureData[j];
              eachpondculture.pond_name = eachrec.name;
              eachpondculture.pond_id = eachrec.id;
              this.pondsDetails_dropdown.push(eachpondculture);
            }
          }
        }
      }
      /*prepare paond array objects*/
      this.batchTransactions = new Array();
      if (feedLogData && feedLogData.length > 0) {
        const batchFeedDataAutofilllConfirm = confirm('Auto fill from last batch entries');
        if (batchFeedDataAutofilllConfirm) {
          for (let i = 0; i < this.pondsDetails_dropdown.length; i++) {
            const eachPond = feedLogData.find((pondObj: any) => pondObj.pond_id == this.pondsDetails_dropdown[i].pond_id);
            if (eachPond) {
              const currentDate = new Date()
              // Calculate time differences for each item
              const withTimeDifferences = eachPond.feedMeal.map((item: any) => {
                  const feedDate = new Date(item.feedLog.last_feed_given_date);
                  const timeDifference = Math.abs(feedDate.getTime() - currentDate.getTime());
                  return { item, timeDifference };
              });
              // Find the minimum time difference
              const minTimeDifference = Math.min(...withTimeDifferences.map(entry => entry.timeDifference));
              // Filter items with the minimum time difference
              const closestObjects = withTimeDifferences.filter((entry: any) => entry.timeDifference === minTimeDifference);
              // Extract the meal item
              eachPond.feedMeal = closestObjects.map((entry: any) => entry.item);
              let eachrec = this.pondsDetails_dropdown[i];
              eachrec.Autofill = true;
              eachrec.FeedLogs = new Array();
              for (let j = 0;j < eachPond.feedMeal.length;j++) {
                  this.feedLog = new FeedLogModel();
                  this.feedLog.isManualUOM = false;
                  this.feedLog.feedMeal = new FeedMealModel();
                  this.feedLog.checkTray = new CheckTrayReadingModel();
                  this.feedLog.feedMeal.feedTypes.push({
                    feed_item_id: eachPond.feedMeal[j].MealFeedType[0].feed_item_id ? eachPond.feedMeal[j].MealFeedType[0].feed_item_id : -1,
                    feed_business_item_id: eachPond.feedMeal[j].MealFeedType[0].feedItemMaster && eachPond.feedMeal[j].MealFeedType[0].feedItemMaster.BusinessItem && eachPond.feedMeal[j].MealFeedType[0].feedItemMaster.BusinessItem.length > 0 ? eachPond.feedMeal[j].MealFeedType[0].feedItemMaster.BusinessItem[0].id : null,
                    feed_uom_id: eachPond.feedMeal[j].MealFeedType[0].feed_uom_id ? eachPond.feedMeal[j].MealFeedType[0].feed_uom_id : -1,
                    feed_qty_given: eachPond.feedMeal[j].MealFeedType[0].feed_uom_id != 3 ? (eachPond.feedMeal[j].MealFeedType[0].feed_qty_given ? eachPond.feedMeal[j].MealFeedType[0].feed_qty_given : null) : eachPond.feedMeal[j].MealFeedType[0].feed_qty_given,
                    mix_item_id: eachPond.feedMeal[j].MealFeedType[0].mix_item_id ? eachPond.feedMeal[j].MealFeedType[0].mix_item_id : -1,
                    mix_business_item_id: eachPond.feedMeal[j].MealFeedType[0].mixItemMaster && eachPond.feedMeal[j].MealFeedType[0].mixItemMaster.BusinessItem && eachPond.feedMeal[j].MealFeedType[0].mixItemMaster.BusinessItem.length > 0 ? eachPond.feedMeal[j].MealFeedType[0].mixItemMaster.BusinessItem[0].id : null,
                    mix_uom_id: eachPond.feedMeal[j].MealFeedType[0].mix_uom_id ? eachPond.feedMeal[j].MealFeedType[0].mix_uom_id : -1,
                    mix_qty_given: eachPond.feedMeal[j].MealFeedType[0].mix_qty_given,
                  })
                  this.feedLog.feedMeal.skip_reason = null;
                  const feedItemsArr = this.feedTypes && this.feedTypes.length > 0 ? this.feedTypes.filter((feedTypeObj: any) => feedTypeObj.id == eachPond.feedMeal[j].MealFeedType[0].feed_item_id) : [];
                  const feedMixArr = this.feedMix && this.feedMix.length > 0 ? this.feedMix.filter((feedMixObj: any) => feedMixObj.id == eachPond.feedMeal[j].MealFeedType[0].mix_item_id) : [];
                  if (feedItemsArr && feedItemsArr.length > 0) {
                    if (feedItemsArr[0].UOMConversion && feedItemsArr[0].UOMConversion.length > 0) {
                      const uomData = [];
                      const feedMeal = this.feedLog.feedMeal.feedTypes[0];
                      for (const eachobj of feedItemsArr[0].UOMConversion) {
                        const feedItemID = feedMeal.feed_item_id;
                        if (feedItemID != 99 && feedItemsArr[0].item_type_id == 1) {
                          if (eachobj.from_units_uom_id != 3 && eachobj.to_units_uom_id != 3) {
                            uomData.push({ id: 3, name: 'Gram', uomadded: true });
                          }
                          if (eachobj.from_units_uom_id != 1 && eachobj.to_units_uom_id != 1) {
                            uomData.push({ id: 1, name: 'Kg', uomadded: true });
                          }
                        }
                        if (feedItemID == 99 && feedItemsArr[0].item_type_id == 1) {
                          if (eachobj.from_units_uom_id == 1 || eachobj.to_units_uom_id == 1) {
                            uomData.push({ id: eachobj.to_units_uom_id, name: eachobj.ToUOM.name });
                          }
                        }
                        if (feedItemID != 99) {
                          uomData.push({ id: eachobj.from_units_uom_id, name: eachobj.FromUOM.name });
                        }
                        if (feedItemID != 99) {
                          uomData.push({ id: eachobj.to_units_uom_id, name: eachobj.ToUOM.name });
                        }
                      }
                      this.feedLog.itemUom = uomData;
                      if (this.feedLog.itemUom.length == 1) {
                        feedMeal.feed_uom_id = this.feedLog.itemUom[0].id;
                      }
                    } else {
                      const uomData = [{ id: feedItemsArr[0].UOMMaster.id, name: feedItemsArr[0].UOMMaster.name }];
                      this.feedLog.itemUom = uomData;
                    }
                  }
                  if (feedMixArr && feedMixArr.length > 0) {
                    let uomData = []; // each time I prepare new items in onchange
                    if (feedMixArr[0].UOMConversion && feedMixArr[0].UOMConversion.length > 0) {
                      for (const eachobj of feedMixArr[0].UOMConversion) {
                        const fromUOMId = eachobj.FromUOM.id;
                        const toUOMId = eachobj.ToUOM.id;
                        if ((fromUOMId != 3 && toUOMId != 3) && (fromUOMId == 1 || toUOMId == 1)) {
                          uomData.push({ id: 3, name: 'Gram', uomadded: true });
                        }
                        if ((fromUOMId != 12 && toUOMId != 12) && (fromUOMId == 5 || toUOMId == 5)) {
                          uomData.push({ id: 12, name: 'ML', uomadded: true });
                        }
                        let toTemptoobj = {
                          'id': eachobj.to_units_uom_id,
                          'name': eachobj.ToUOM.name
                        };
                        uomData.push(toTemptoobj);
                        let fromTemptoobj = {
                          'id': eachobj.from_units_uom_id,
                          'name': eachobj.FromUOM.name
                        };
                        uomData.push(fromTemptoobj);
                      }
                      this.feedLog.mixUom = uomData;
                    } else {
                      uomData.push({ id: feedMixArr[0].UOMMaster.id, name: feedMixArr[0].UOMMaster.name });
                      this.feedLog.mixUom = uomData;
                    }
                  }
                  this.feedLog.checkTray.check_tray_feed_given = eachPond.feedMeal[j].checkTray[0].check_tray_feed_given;
                  this.feedLog.checkTray.check_tray_feed_consumed = eachPond.feedMeal[j].checkTray[0].check_tray_feed_consumed;
                  this.feedLog.checkTray.CTIR = false;
                  this.feedLog.checkTray.imagecount = 0;
                  this.feedLog.feeditem_name = feedItemsArr && feedItemsArr.length > 0 ?  feedItemsArr[0].item_name + '' + (feedItemsArr[0].sub_type ? '-' + feedItemsArr[0].sub_type : '') + '' + ((feedItemsArr[0] && feedItemsArr[0].UOMConversion.length > 0 && feedItemsArr[0].UOMConversion[0].to_value) ? '-' + (feedItemsArr[0] && feedItemsArr[0].UOMConversion.length > 0 && feedItemsArr[0].UOMConversion[0].to_value) : '') + ((feedItemsArr[0] && feedItemsArr[0].UOMConversion.length > 0 && feedItemsArr[0].UOMConversion[0].ToUOM.name) ? (feedItemsArr[0] && feedItemsArr[0].UOMConversion.length > 0 && feedItemsArr[0].UOMConversion[0].ToUOM.name) : '') : null;
                  this.feedLog.mixitem_name = feedMixArr && feedMixArr.length > 0 ? feedMixArr[0].item_name + '' + (feedMixArr[0].sub_type ? '-' + feedMixArr[0].sub_type : '') + '' + ((feedMixArr[0] && feedMixArr[0].UOMConversion.length > 0 && feedMixArr[0].UOMConversion[0].to_value) ? '-' + (feedMixArr[0] && feedMixArr[0].UOMConversion.length > 0 && feedMixArr[0].UOMConversion[0].to_value) : '') + ((feedMixArr[0] && feedMixArr[0].UOMConversion.length > 0 && feedMixArr[0].UOMConversion[0].ToUOM.name) ? (feedMixArr[0] && feedMixArr[0].UOMConversion.length > 0 && feedMixArr[0].UOMConversion[0].ToUOM.name) : '') : null;
                  this.feedLog.name = eachPond.feedMeal[j].MealFeedType[0].feedUomMaster ? eachPond.feedMeal[j].MealFeedType[0].feedUomMaster.name : null;
                  this.feedLog.mixuomname = eachPond.feedMeal[j].MealFeedType[0].mixUomMaster ? eachPond.feedMeal[j].MealFeedType[0].mixUomMaster.name : null;
                  this.batchTransaction.FeedLogs.push(this.feedLog);
                  eachrec.FeedLogs.push(this.feedLog);
                }
              } else {
              let eachrec = this.pondsDetails_dropdown[i];
              eachrec.Autofill = false;
              eachrec.FeedLogs = new Array();
              this.feedLog = new FeedLogModel();
              this.feedLog.isManualUOM = false;
              this.feedLog.feedMeal = new FeedMealModel();
              this.feedLog.feedMeal.feedTypes.push({
                feed_item_id: -1,
                feed_business_item_id: -1,
                feed_uom_id: -1,
                feed_qty_given: null,
                mix_item_id: -1,
                mix_business_item_id: -1,
                mix_uom_id: -1,
                mix_qty_given: null,
              });
              this.feedLog.checkTray = new CheckTrayReadingModel();
              this.feedLog.checkTray.CTIR = false;
              this.feedLog.checkTray.imagecount = 0;
              this.batchTransaction.FeedLogs.push(this.feedLog);
              eachrec.FeedLogs.push(this.feedLog);
            }
          }
        } else {
          this.commonSectionPonds();
        }
      } else {
         this.commonSectionPonds();
      }
      /*prepare paond array objects end*/
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method commonSectionPonds
   * @description Initialize empty meal entries for all ponds in section (no auto-fill scenario)
   *
   * Execution flow:
   * For each pond in pondsDetails_dropdown:
   * 1. Set Autofill = false (no previous batch data)
   * 2. Initialize FeedLogs array
   * 3. Create single empty FeedLogModel with uninitialized feed type (-1 IDs)
   * 4. Create empty CheckTrayReadingModel with CTIR = false, imagecount = 0
   * 5. Add to both batchTransaction.FeedLogs and pond's FeedLogs array
   *
   * @returns {void}
   * @side-effect
   * - Populates: pondsDetails_dropdown[].FeedLogs for each pond
   * - Populates: batchTransaction.FeedLogs with initial empty entries
   * - Sets: Autofill = false for all ponds
   */
  public commonSectionPonds() {
    for (let k = 0; k < this.pondsDetails_dropdown.length; k++) {
      let eachrec = this.pondsDetails_dropdown[k];
      eachrec.Autofill = false;
      eachrec.FeedLogs = new Array();
      this.feedLog = new FeedLogModel();
      this.feedLog.isManualUOM = false;
      this.feedLog.feedMeal = new FeedMealModel();
      this.feedLog.feedMeal.feedTypes.push({
        feed_item_id: -1,
        feed_business_item_id: -1,
        feed_uom_id: -1,
        feed_qty_given: null,
        mix_item_id: -1,
        mix_business_item_id: -1,
        mix_uom_id: -1,
        mix_qty_given: null,
      });
      this.feedLog.checkTray = new CheckTrayReadingModel();
      this.feedLog.checkTray.CTIR = false;
      this.feedLog.checkTray.imagecount = 0;
      this.batchTransaction.FeedLogs.push(this.feedLog);
      eachrec.FeedLogs.push(this.feedLog);
    }
  }

  /**
   * @method getFeedTypes
   * @async
   * @description Fetch and cache feed types for the selected business entity
   *
   * Execution flow:
   * 1. Parse businessEntityById from localStorage
   * 2. If business has providers, use first provider's business ID
   * 3. Otherwise use the business entity's own ID
   * 4. Call ItemMasterService to fetch feed types
   * 5. Format item names: item_name + sub_type + UOM conversion to_value + UOM name
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Populates: feedTypes array with formatted item names
   * - Sets: businessEntity from localStorage
   * - Updates: paramsbusinessId if provider relationships exist
   * - Formats: Each feed type with display name including sub_type and UOM info
   */
  public async getFeedTypes() {
    try {
      this.businessEntity = JSON.parse(localStorage.getItem('businessEntityById'));
      if (this.businessEntity.providerID.length > 0) {
        this.paramsbusinessId = this.businessEntity.providerID[0].businessID;
      } else {
        this.paramsbusinessId = this.businessEntity.id;
      }
      this.itemMasters = await this.itemMastersService.getFeedTypes(this.paramsbusinessId);
      this.feedTypes = this.itemMasters;
      for (let k = 0; k < this.feedTypes.length; k++) {
        let eachItem = this.feedTypes[k];
        this.feedTypes[k].item_name = eachItem.item_name + '' + (eachItem.sub_type ? '-' + eachItem.sub_type : '') + '' + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) ? '-' + (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) : '') + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) ? (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) : '');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getFeedMix
   * @async
   * @description Fetch and cache feed mixes for the selected business entity
   *
   * Execution flow:
   * 1. Parse businessEntityById from localStorage
   * 2. If business has providers, use first provider's business ID
   * 3. Otherwise use the business entity's own ID
   * 4. Call ItemMasterService to fetch feed mixes
   * 5. Format item names: item_name + sub_type + UOM conversion to_value + UOM name
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   *
   * @side-effect
   * - Populates: feedMix array with formatted item names
   * - Sets: businessEntity from localStorage
   * - Updates: paramsbusinessId if provider relationships exist
   * - Formats: Each feed mix with display name including sub_type and UOM info
   */
  public async getFeedMix() {
    try {
      this.businessEntity = JSON.parse(localStorage.getItem('businessEntityById'));
      if (this.businessEntity.providerID.length > 0) {
        this.paramsbusinessId = this.businessEntity.providerID[0].businessID;
      } else {
        this.paramsbusinessId = this.businessEntity.id;
      }
      this.itemMasters = await this.itemMastersService.getFeedMix(this.paramsbusinessId);
      this.feedMix = this.itemMasters;
      for (let k = 0; k < this.feedMix.length; k++) {
        let eachItem = this.feedMix[k];
        this.feedMix[k].item_name = eachItem.item_name + '' + (eachItem.sub_type ? '-' + eachItem.sub_type : '') + '' + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) ? '-' + (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) : '') + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) ? (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) : '');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /*get feed mix uom data*/
  /**
   * @method getFeedMixIdsUomData
   * @description Process selected feed mix and populate available mix UOM conversions
   *
   * Logic:
   * 1. Clear mixUom array for current meal entry
   * 2. Filter feedMix array to find the selected mix item ID
   * 3. Call prepareMixUOM to build mix UOM conversion list
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Clears: mixUom array
   * - Populates: mixUom with filtered mix UOM options via prepareMixUOM()
   */
  public getFeedMixIdsUomData(index: number, j: number) {
    this.pondsDetails_dropdown[j].FeedLogs[index].mixUom = new Array();
    let feedMixIdarr = _.filter(this.feedMix, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_item_id;
    });
    if (feedMixIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_item_id = feedMixIdarr[0].id;
      this.prepareMixUOM(feedMixIdarr[0], index, j);
    }
  }

  /**
   * @method prepareMixUOM
   * @description Build available UOM options for a selected feed mix based on its UOM conversions
   *
   * Logic:
   * 1. Iterate through feed mix's UOMConversion array
   * 2. Add special UOM combinations:
   *    - Add Gram (ID 3) if needed for Kg conversions
   *    - Add ML (ID 12) if needed for Liter conversions
   * 3. Add all to_units and from_units from conversion rules
   * 4. If single UOM exists, auto-select it
   *
   * @param {any} itemsArray - Feed mix object with UOMConversion array
   * @param {number} index - Meal entry index
   * @param {number} j - Pond index
   * @returns {void}
   * @side-effect
   * - Populates: pondsDetails_dropdown[j].FeedLogs[index].mixUom array
   * - Sets: mix_uom_id if single UOM available
   */
  public prepareMixUOM(itemsArray: any, index: number, j: number) {
    let uomData = []; // each time i prepare new items in onchange
    if (itemsArray && itemsArray.UOMConversion && itemsArray.UOMConversion.length > 0) {
      let uomdataArr = itemsArray.UOMConversion;
      for (let i = 0; i < uomdataArr.length; i++) {
        let eachobj = uomdataArr[i];
        if (eachobj['FromUOM']['id'] != 3 && eachobj['ToUOM']['id'] != 3 && (eachobj['FromUOM']['id'] == 1 || eachobj['ToUOM']['id'] == 1)) {
          uomData.push({id: 3, name: 'Gram', 'uomadded': true});
        }
        if (eachobj['FromUOM']['id'] != 12 && eachobj['ToUOM']['id'] != 12 && (eachobj['FromUOM']['id'] == 5 || eachobj['ToUOM']['id'] == 5)) {
          uomData.push({id: 12, name: 'ML', 'uomadded': true});
        }
        let toTemptoobj = {
          'id': eachobj.to_units_uom_id,
          'name': eachobj.ToUOM.name
        };
        uomData.push(toTemptoobj);
        let fromTemptoobj = {
          'id': eachobj.from_units_uom_id,
          'name': eachobj.FromUOM.name
        };
        uomData.push(fromTemptoobj);
        this.pondsDetails_dropdown[j].FeedLogs[index].mixUom = uomData;
      }
    } else {
      uomData.push({id: itemsArray['UOMMaster']['id'], name: itemsArray['UOMMaster']['name']});
      this.pondsDetails_dropdown[j].FeedLogs[index].mixUom = uomData;
    }
  }

  /**
   * @method initializedatePicker
   * @description Initialize jQuery date picker for batch entry date selection
   *
   * Features:
   * 1. Set maximum date to today (prevents future date selection)
   * 2. On date selection, format date as DD-MMM-YYYY (e.g., "15-Jan-2025")
   * 3. Set min/max constraints between start and end date pickers
   * 4. Parse selected date and trigger cumulative feed calculation
   *
   * @param {any} _self - Component context (this) for calling calculateCumulativeFeed
   * @returns {void}
   * @side-effect
   * - Initializes jQuery date picker on #startdate element
   * - Formats and sets batchEntrtyDate on selection
   * - Calls calculateCumulativeFeed() after date selection
   */
  public initializedatePicker(_self: any) {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function(selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('#startdate').val(parsedDate);
        _self.calculateCumulativeFeed();
      }
    });
  }

  /**
   * @method addNewFeedLogButton
   * @description Validate last meal entry and add a new blank meal entry to pond's feed logs
   *
   * Validation checks (in order):
   * 1. Feed type must be selected (feed_item_id != -1)
   * 2. If skip reason feed type (ID 99), skip_reason must be provided
   * 3. Feed quantity must be provided (not null)
   * 4. Feed quantity must be valid (not 0 or '-')
   * 5. Feed UOM must be selected (feed_uom_id != -1)
   * 6. If feed mix is selected, mix quantity must be provided
   * 7. If feed mix is selected, mix quantity must be valid (not 0 or '-')
   * 8. If feed mix is selected, mix UOM must be selected (mix_uom_id != -1)
   *
   * Success action: Clone last meal entry and append to FeedLogs array
   * Each failed validation displays error toast and returns early
   *
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Displays error toast if any validation fails
   * - Clones last FeedLog object and appends to pond's FeedLogs array if all validations pass
   */
  public addNewFeedLogButton(j: number) {
    if (this.pondsDetails_dropdown[j].FeedLogs.length > 0) {
      if (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['feed_item_id'] == -1) {
        this.toasterService.error('Select feed type');
        return;
      } else if ((this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['feed_item_id'] == 99) && (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['skip_reason'].length == 0)) {
        this.toasterService.error('Enter reason');
        return;
      } else if (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['feed_qty_given'] == null) {
        this.toasterService.error('Enter vfeed quantity');
        return;
      } else if (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['feed_qty_given'] == 0 ||
        this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['feed_qty_given'] == '-') {
        this.toasterService.error('Enter valid feed quantity');
        return;
      } else if (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['feed_uom_id'] == -1) {
        this.toasterService.error('Select UOM');
        return;
      } else if (!(this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_item_id'] == -1) && (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_qty_given'] == '')) {
        this.toasterService.error('Enter Mix quantity');
        return;
      } else if (!(this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_item_id'] == -1) && ((this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_qty_given'] == 0) || this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_qty_given'] == '-')) {
        this.toasterService.error('Enter Mix quantity');
        return;
      } else if (!(this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_item_id'] == -1) && (this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1]['feedMeal']['feedTypes'][0]['mix_uom_id'] == -1)) {
        this.toasterService.error('Select Mix UOM');
        return;
      }
    }
    if (this.pondsDetails_dropdown[j].FeedLogs.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs.push(JSON.parse(JSON.stringify(this.pondsDetails_dropdown[j].FeedLogs[this.pondsDetails_dropdown[j].FeedLogs.length - 1])));
    }
  }

  /**
   * @method removeObj
   * @description Remove a meal entry from a pond's feed logs
   *
   * Logic:
   * 1. Check if only one meal entry exists (minimum required)
   * 2. If yes, show warning toast and prevent deletion
   * 3. If multiple entries exist, remove the entry at index i
   * 4. Show success toast after deletion
   *
   * @param {number} i - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {boolean} - False if deletion prevented, true if deleted
   * @side-effect
   * - Removes meal entry from pondsDetails_dropdown[j].FeedLogs array
   * - Displays warning or success toast
   */
  public removeObj(i: number, j: number) {
    if (this.pondsDetails_dropdown[j].FeedLogs.length == 1) {
      this.toasterService.warning('Can\'t delete the row when there is only one row');
      return false;
    } else {
      this.pondsDetails_dropdown[j].FeedLogs.splice(i, 1);
      this.toasterService.success('Row deleted successfully', '', {
        timeOut: 2000
      });
      return true;
    }
  }

  /**
   * @method removePond
   * @description Remove a pond card from the batch entry
   *
   * Logic:
   * 1. Check if only one pond exists (minimum required)
   * 2. If yes, show warning toast and prevent deletion
   * 3. If multiple ponds exist, remove the pond at index i
   * 4. Show success toast after deletion
   *
   * @param {number} i - Pond index in pondsDetails_dropdown array
   * @returns {boolean} - False if deletion prevented, true if deleted
   * @side-effect
   * - Removes pond from pondsDetails_dropdown array
   * - Displays warning or success toast
   */
  public removePond(i: number) {
    if (this.pondsDetails_dropdown.length == 1) {
      this.toasterService.warning('Can\'t delete the pond card when there is only one pond');
      return false;
    } else {
      this.pondsDetails_dropdown.splice(i, 1);
      this.toasterService.success('Card deleted successfully', '', {
        timeOut: 2000
      });
      return true;
    }
  }

  /**
   * @method applyToAllPonds
   * @description Copy meal data from one pond to all other ponds in the batch
   *
   * Execution flow:
   * 1. Get FeedLogs from selected pond (deep clone via JSON stringify/parse)
   * 2. Iterate through all ponds in pondsDetails_dropdown
   * 3. Clear each pond's existing FeedLogs
   * 4. Clone and copy meal entries from source pond to all ponds
   *
   * Use case: When multiple ponds need identical meal structure, allows user to
   * define meals once and apply uniformly across all ponds.
   *
   * @param {number} j - Source pond index in pondsDetails_dropdown array
   * @returns {void}
   * @throws {Error} Caught and displayed via toasterService.error
   * @side-effect
   * - Clears and repopulates FeedLogs for all ponds except potentially the source
   * - Uses deep clone to prevent object reference issues
   */
  public applyToAllPonds(j: number) {
    try {
      let myDataDetails = this.pondsDetails_dropdown[j];
      let myData = JSON.parse(JSON.stringify(myDataDetails.FeedLogs));
      this.pondsDetails_dropdown.filter(pond => {
        pond.FeedLogs = new Array();
        for (let i = 0; i < myData.length; i++) {
          pond.FeedLogs.push(JSON.parse(JSON.stringify(myData[i])));
        }
      });
    } catch (error) {
      this.toasterService.error(error);
    }
  }

  /**
   * @method feedTypeName
   * @description Lookup and set display name for selected feed type
   *
   * Logic:
   * 1. Filter feedTypes array to find matching feed_item_id
   * 2. Extract item_name from matched feed type
   * 3. Set feeditem_name property for display
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Sets: pondsDetails_dropdown[j].FeedLogs[index].feeditem_name
   */
  public feedTypeName(index: number, j: number) {
    let feedItemIdarr = _.filter(this.feedTypes, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_item_id;
    });
    if (feedItemIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].feeditem_name = feedItemIdarr[0].item_name;
    }
  }

  /**
   * @method getUomNameByUomId
   * @description Set selected UOM ID and lookup corresponding UOM display name
   *
   * Logic:
   * 1. Set feed_uom_id to the selected UOM ID
   * 2. Filter itemUom array to find matching UOM
   * 3. Set name property to UOM display name (or null if not found)
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @param {number} selectedUOMId - The selected unit of measure ID
   * @returns {void}
   * @side-effect
   * - Sets: feed_uom_id and name properties
   */
  public getUomNameByUomId(index: number, j: number, selectedUOMId: number) {
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_uom_id = selectedUOMId;
    let uomIdarr = _.filter(this.pondsDetails_dropdown[j].FeedLogs[index].itemUom, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_uom_id;
    });
    if (uomIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].name = uomIdarr[0].name;
    } else {
      this.pondsDetails_dropdown[j].FeedLogs[index].name = null;
    }
  }

  /**
   * @method getFeedMixNameById
   * @description Lookup and set display name for selected feed mix
   *
   * Logic:
   * 1. Filter feedMix array to find matching mix_item_id
   * 2. Extract item_name from matched feed mix
   * 3. Set mixitem_name property for display
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Sets: pondsDetails_dropdown[j].FeedLogs[index].mixitem_name
   */
  public getFeedMixNameById(index: number, j: number) {
    let feedMixItemIdarr = _.filter(this.feedMix, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_item_id;
    });
    if (feedMixItemIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].mixitem_name = feedMixItemIdarr[0].item_name;
    }
  }

  /**
   * @method getMixUomNameByUomId
   * @description Lookup and set display name for selected mix UOM and track manual UOM flag
   *
   * Logic:
   * 1. Filter mixUom array to find matching mix_uom_id
   * 2. Extract UOM name from matched mix UOM
   * 3. Set mixuomname property for display
   * 4. Set isManualUOM flag if uomadded property is true (user-added conversion)
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Sets: mixuomname and isManualUOM properties
   * - isManualUOM used during checkUOM for conversion calculation
   */
  public getMixUomNameByUomId(index: number, j: number) {
    let uomIdarr = _.filter(this.pondsDetails_dropdown[j].FeedLogs[index].mixUom, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_uom_id;
    });
    if (uomIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].mixuomname = uomIdarr[0].name;
      this.pondsDetails_dropdown[j].FeedLogs[index].isManualUOM = uomIdarr[0].uomadded ? true : false;
    }
  }

  /**
   * @method calculateCumulativeFeed
   * @async
   * @description Fetch previous feed log data for selected date to show cumulative consumption history
   *
   * Execution flow:
   * 1. Build array of culture_id and pond_id from pondsDetails_dropdown
   * 2. Get selected batch entry date from jQuery date picker
   * 3. Format date as YYYY-MM-DD
   * 4. Call FeedLogService to fetch feed logs for these ponds on the selected date
   * 5. Store results in both feedLogs and LatestFeedLogs for reference
   *
   * Use case: Show user what was previously fed on same date to aid decision-making
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   * @side-effect
   * - Populates: feedLogs and LatestFeedLogs arrays
   * - Reads: batchEntrtyDate from date picker
   */
  public async calculateCumulativeFeed() {
    /*prepare tank ids array*/
    let pondIdsarr = [];
    if (this.pondsDetails_dropdown && this.pondsDetails_dropdown.length > 0) {
      for (let i = 0; i < this.pondsDetails_dropdown.length; i++) {
        let eachselectobj = this.pondsDetails_dropdown[i];
        let newobj = {
          culture_id: eachselectobj.id,
          pond_id: eachselectobj.pond_id,
        };
        pondIdsarr.push(newobj);
      }
    }
    this.batchEntrtyDate = $('#startdate').val();
    if ($('#startdate').val() != '') {
      this.batchEntrtyDate = new Date(this.batchEntrtyDate);
      this.batchEntrtyDate = (this.batchEntrtyDate.getFullYear() > 0 ? this.batchEntrtyDate.getFullYear() : this.batchEntrtyDate.getFullYear() * -1) + '-' + (this.batchEntrtyDate.getMonth() + 1) + '-' + this.batchEntrtyDate.getDate();
    }
    const selectedDate = moment(this.batchEntrtyDate).format('YYYY-MM-DD');
    let inputData = new FeedLogModel();
    inputData.pondIds = pondIdsarr;
    inputData.date = selectedDate;
    try {
      this.feedLogs = await this.feedLogService.getLatestFeedLogData(inputData);
      this.LatestFeedLogs = this.feedLogs;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method priviewButton
   * @description Validate form data and transition to preview mode for user review
   *
   * Validation sequence:
   * 1. Check required dropdowns (business, farmsite, section, ponds)
   * 2. For each pond and meal entry:
   *    - If skip reason item (ID 99), skip_reason must be provided
   *    - If regular feed, feed_qty_given must be valid (not 0 or '-')
   *    - If regular feed, mix_qty_given must be valid if mix is selected
   *    - Check tray consumed must not exceed check tray given
   * 3. Count meals with all required fields populated
   * 4. Ensure at least one complete meal was entered
   *
   * On success:
   * - Lock all input fields (noFeedTypeEditable, nogeedQtyEditable, etc. = false)
   * - Show edit and submit buttons
   * - Hide preview button
   *
   * @returns {void}
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   * @side-effect
   * - Sets all field editability flags to false
   * - Sets: editButton=true, submitButton=true, previewButton=false
   * - Displays error toasts for each validation failure
   */
  public priviewButton() {
    try {
      let mealsEntered = 0; // the meals data
      let noOfMeals = 0; // mydata.length where feed_item_id or feed_qty_given or uom_id is entered
      for (let i = 0; i < this.pondsDetails_dropdown.length; i++) {
        if (!this.paramsbusinessId) {
          this.toasterService.error('Please select farm');
          return;
        }
        if (!this.paramsfarmsiteId) {
          this.toasterService.error('Please select farmsite');
          return;
        }
        if (!this.paramssectionId) {
          this.toasterService.error('Please select section');
          return;
        }
        if (!this.pondsDetails_dropdown[i].pond_id) {
          this.toasterService.error('Please select ponds');
          return;
        }
        for (let j = 0; j < this.pondsDetails_dropdown[i].FeedLogs.length; j++) {
          let eachObj = this.pondsDetails_dropdown[i].FeedLogs[j];
          if (eachObj.feedMeal.feedTypes[0].feed_item_id == 99 && eachObj.feedMeal.skip_reason.length == 0) {
            this.toasterService.error('Please enter skip reason');
            return;
          }
          if (eachObj.feedMeal.feedTypes[0].feed_item_id != 99 && (eachObj.feedMeal.feedTypes[0].feed_qty_given == 0 || eachObj.feedMeal.feedTypes[0].feed_qty_given == '-')) {
            this.toasterService.error('Enter valid feed quantity');
            return;
          }
          if (eachObj.feedMeal.feedTypes[0].feed_item_id != 99 && (eachObj.feedMeal.feedTypes[0].mix_qty_given == 0 || eachObj.feedMeal.feedTypes[0].mix_qty_given == '-')) {
            this.toasterService.error('Enter valid feed mix Quanitity');
            return;
          }
          if ((eachObj.checkTray.check_tray_feed_given && eachObj.checkTray.check_tray_feed_consumed) && (Number(eachObj.checkTray.check_tray_feed_given) < Number(eachObj.checkTray.check_tray_feed_consumed))) {
            this.toasterService.error('Check tray consumed is more than check tray given');
            return;
          }
          if ((eachObj.feedMeal.feedTypes[0].feed_item_id != -1 || eachObj.feedMeal.feedTypes[0].feed_qty_given != undefined || eachObj.feedMeal.feedTypes[0].feed_uom_id != -1)) {
            noOfMeals += 1;
          }
          if ((eachObj.feedMeal.feedTypes[0].feed_item_id != -1 && eachObj.feedMeal.feedTypes[0].feed_qty_given != 0 && eachObj.feedMeal.feedTypes[0].feed_uom_id != -1)) {
            this.valueExit = true;
            mealsEntered += 1;
            // break;
          } else {
            this.valueExit = false;
          }
        }
      }
      if ((mealsEntered != noOfMeals) || mealsEntered == 0) {
        this.toasterService.error('Provide meal data');
        return;
      }
      this.noFeedTypeEditable = false;
      this.nogeedQtyEditable = false;
      this.noreasonEditable = false;
      this.lastMealEditable = true;
      this.noMealEditText = false;
      this.noAddIconEditable = false;
      this.noUomEditable = false;
      this.noMixUomEditable = false;
      this.noFeedMixTypeEditable = false;
      this.noMixQtyEditable = false;
      this.nocheckFeedGivenEditable = false;
      this.nocheckFeedConsumedEditable = false;
      this.noMealDeleteIconEditable = false;
      this.noApplyAllPondsEditable = false;
      this.noCardDeleteEditable = false;
      this.editButton = true;
      this.submitButton = true;
      this.previewButton = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method feedLogEditIcon
   * @description Revert form from preview mode back to edit mode
   *
   * Execution flow:
   * 1. Unlock all input fields by setting editability flags to true
   * 2. Hide submit and edit buttons
   * 3. Show preview button
   *
   * Use case: User wants to modify data after preview, returns to edit state
   *
   * @returns {void}
   * @side-effect
   * - Sets all field editability flags to true
   * - Sets: submitButton=false, editButton=false, previewButton=true
   */
  public feedLogEditIcon() {
    this.noMealEditText = true;
    this.noAddIconEditable = true;
    this.noFeedTypeEditable = true;
    this.nogeedQtyEditable = true;
    this.noreasonEditable = true;
    this.lastMealEditable = true;
    this.noUomEditable = true;
    this.noMixUomEditable = true;
    this.noFeedMixTypeEditable = true;
    this.noMixQtyEditable = true;
    this.nocheckFeedGivenEditable = true;
    this.nocheckFeedConsumedEditable = true;
    this.noMealDeleteIconEditable = true;
    this.noApplyAllPondsEditable = true;
    this.noCardDeleteEditable = true;
    this.submitButton = false;
    this.editButton = false;
    this.previewButton = true;
  }

  /**
   * @method getFeedBusinessItemId
   * @description Lookup and set business item ID for selected feed type, reset dependent fields
   *
   * Execution flow:
   * 1. Clear itemUom array
   * 2. Reset all dependent fields (UOM, quantity, mix, check tray) to defaults
   * 3. Filter feedTypes to find BusinessItem for selected feed type
   * 4. Set feed_business_item_id from BusinessItem[0].id
   * 5. If skip reason item (ID 99), auto-set feed_qty_given = 0 (skip has no quantity)
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Clears and resets itemUom, feed_uom_id, feed_qty_given, skip_reason
   * - Resets mix_item_id, mix_uom_id, mix_qty_given
   * - Resets check_tray_feed_given and check_tray_feed_consumed
   * - Sets: feed_business_item_id
   * - Sets: feed_qty_given=0 if feed type is skip reason (99)
   */
  public getFeedBusinessItemId(index: number, j: number) {
    this.pondsDetails_dropdown[j].FeedLogs[index].itemUom = new Array();
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_uom_id = -1;
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_qty_given = null;
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.skip_reason = null;
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_item_id = -1;
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_uom_id = -1;
    this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_qty_given = null;
    this.pondsDetails_dropdown[j].FeedLogs[index].checkTray.check_tray_feed_given = null;
    this.pondsDetails_dropdown[j].FeedLogs[index].checkTray.check_tray_feed_consumed = null;
    let businessItemIdarr = _.filter(this.feedTypes, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_item_id;
    });
    if (businessItemIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_business_item_id = businessItemIdarr[0].BusinessItem[0].id;
    }
    if (this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_item_id == 99) {
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_qty_given = 0;
    }
  }

  /**
   * @method getFeedMixBusinessItemId
   * @description Lookup and set business item ID for selected feed mix, clear mix if skip feed selected
   *
   * Execution flow:
   * 1. Filter feedMix array to find BusinessItem for selected mix
   * 2. Set mix_business_item_id from BusinessItem[0].id
   * 3. If feed type is skip reason (ID 99), clear mix selection (skip reason cannot have mix)
   *
   * @param {number} index - Meal entry index within pond's FeedLogs array
   * @param {number} j - Pond index in pondsDetails_dropdown array
   * @returns {void}
   * @side-effect
   * - Sets: mix_business_item_id
   * - If feed type is 99: Resets mix_item_id, mix_uom_id, mix_qty_given to defaults
   */
  public getFeedMixBusinessItemId(index: number, j: number) {
    let businessItemIdarr = _.filter(this.feedMix, (o) => {
      return o.id == this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_item_id;
    });
    if (businessItemIdarr.length > 0) {
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_business_item_id = businessItemIdarr[0].BusinessItem[0].id;
    }
    if (this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].feed_item_id == 99) {
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_item_id = -1;
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_uom_id = -1;
      this.pondsDetails_dropdown[j].FeedLogs[index].feedMeal.feedTypes[0].mix_qty_given = null;
    }
  }

  /*add btn diabled*/
  lastmeal: boolean;

  /**
   * @method onLastmealChecked
   * @description Track checkbox state for marking last meal in sequence
   *
   * Logic: Store the checked state from the last meal checkbox
   *
   * @param {any} event - Checkbox change event
   * @returns {void}
   * @side-effect
   * - Sets: lastmeal flag with checkbox state
   */
  public onLastmealChecked(event) {
    this.lastmeal = event.target.checked;
  }

  /**
   * @method checkUOM
   * @async
   * @description Validate UOM conversions for all meal entries, then create batch transaction if valid
   *
   * Execution flow:
   * 1. Build transaction request with feed data from all ponds and meals
   * 2. For each meal entry:
   *    - Create FeedTypeModel with all conversion details
   *    - Handle manual UOM adjustments (mix UOM ID conversion)
   *    - Lookup feed type and mix type master data
   *    - Set feed and mix quantities with proper UOM formatting
   * 3. Call ItemMasterService.checkUOMConversion() to validate all conversions
   * 4. If validation passes, call addFeedLog() to persist transactions
   * 5. If validation fails or request exceeds size limit, show error and reenable submit button
   *
   * @returns {Promise<void>}
   * @async
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   * @side-effect
   * - Sets: isCommitDisabled=true during processing
   * - Sets: disableDownload=true on error
   * - Calls: addFeedLog() if UOM validation passes
   * - On error: Sets disableDownload=true, restores submit buttons
   */
  public async checkUOM() {
    this.itemMaster.feedTypes = new Array();
    try {
      const formattedDate = this.formatDateToYMD(this.date_time);
      let addMMReq = [];
      for (let i = 0; i <= this.pondsDetails_dropdown.length - 1; i++) {
        let eachPondObj = JSON.parse(JSON.stringify(this.pondsDetails_dropdown[i]));
        for (let j = 0; j <= eachPondObj.FeedLogs.length - 1; j++) {
          let eachMealObj = JSON.parse(JSON.stringify(eachPondObj.FeedLogs[j]));
          this.itemMaster = new ItemMasterModel();
          this.itemMaster.feedTypes = new Array();
          if (eachMealObj.feedMeal.feedTypes[0].feed_item_id != -1) {
            this.itemMaster.stage = 'Growout';
            this.itemMaster.pond_id = eachPondObj.pond_id;
            this.itemMaster.pond_culture_id = eachPondObj.id;
            let mixuomid = eachMealObj.isManualUOM ? (eachMealObj.feedMeal.feedTypes[0].mix_uom_id == 12 ? 5 : (eachMealObj.feedMeal.feedTypes[0].mix_uom_id == 3 ? 1 : eachMealObj.feedMeal.feedTypes[0].mix_uom_id)) : eachMealObj.feedMeal.feedTypes[0].mix_uom_id;
            let mixqty = eachMealObj.isManualUOM ? (eachMealObj.feedMeal.feedTypes[0].mix_qty_given / 1000) : eachMealObj.feedMeal.feedTypes[0].mix_qty_given;
            this.feedType = new FeedTypeModel();
            this.feedType.pond_id = this.itemMaster.pond_id;
            this.feedType.pond_culture_id = this.itemMaster.pond_culture_id;
            this.feedType.feed_item_id = Number(eachMealObj.feedMeal.feedTypes[0].feed_item_id);
            this.feedType.feedName = eachMealObj.feeditem_name;
            this.feedType.mix_item_id = eachMealObj.feedMeal.feedTypes[0].mix_item_id != -1 && eachMealObj.feedMeal.feedTypes[0].mix_item_id != '' ? Number(eachMealObj.feedMeal.feedTypes[0].mix_item_id) : null;
            this.feedType.feedMix = eachMealObj.mixitem_name ? eachMealObj.mixitem_name : null;
            this.feedType.feed_business_item_id = Number(eachMealObj.feedMeal.feedTypes[0].feed_business_item_id);
            if (eachMealObj.itemUom[0]['uomadded'] == true && eachMealObj.itemUom[0].id == 3 && eachMealObj.feedMeal.feedTypes[0].feed_uom_id == 3) {
              // this.feedType.feed_qty_given = parseFloat(eachMealObj.feedMeal.feedTypes[0].feed_qty_given) / 1000;
              this.feedType.feed_qty_given = parseFloat(eachMealObj.feedMeal.feedTypes[0].feed_qty_given);
              this.feedType.feed_uom_id = 3;
            } else if (eachMealObj.itemUom['uomadded'] == true && eachMealObj.itemUom[0].id == 1 && eachMealObj.feedMeal.feedTypes[0].feed_uom_id == 1) {
              // this.feedType.feed_qty_given = parseFloat(eachMealObj.feedMeal.feedTypes[0].feed_qty_given)) * 1000;
              this.feedType.feed_qty_given = parseFloat(eachMealObj.feedMeal.feedTypes[0].feed_qty_given);
              this.feedType.feed_uom_id = 1;
            } else {
              this.feedType.feed_qty_given = parseFloat(eachMealObj.feedMeal.feedTypes[0].feed_qty_given);
              this.feedType.feed_uom_id = Number(eachMealObj.feedMeal.feedTypes[0].feed_uom_id);
            }
            let feedUomName = eachMealObj.itemUom.filter(uomName => uomName.id == this.feedType.feed_uom_id);
            this.feedType.feedUOM = feedUomName[0].name;
            this.feedType.mix_business_item_id = eachMealObj.feedMeal.feedTypes[0].mix_business_item_id != -1 && eachMealObj.feedMeal.feedTypes[0].mix_business_item_id != '' ? Number(eachMealObj.feedMeal.feedTypes[0].mix_business_item_id) : null;
            if (mixqty) {
              this.feedType.mix_qty_given = parseFloat(mixqty);
            } else {
              this.feedType.mix_qty_given = null;
            }
            this.feedType.mix_uom_id = Number(mixuomid);
            let feedMixUomName = eachMealObj.mixUom.filter(mixUomName => mixUomName.id == this.feedType.mix_uom_id);
            if (feedMixUomName.length > 0) {
              this.feedType.mixUom = feedMixUomName[0].name;
            } else {
              this.feedType.mixUom = null;
            }
            this.itemMaster.feedTypes.push(this.feedType);
            addMMReq.push(this.itemMaster);
          }
        }
      }
      this.newTransactionReq = {
        'farm_site_id': this.paramsfarmsiteId,
        'txn_from_date': formattedDate,
        'txn_to_date': formattedDate,
        'feedTransactions': addMMReq
      };
      if (this.newTransactionReq) {
        this.isCommitDisabled = true;
        try {
          await this.itemMastersService.checkUOMConversion(this.newTransactionReq);
          await this.addFeedLog(this.newTransactionReq);
        } catch (error) {
          this.commonMethodsService.userFriendlyAlertMessages(error);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.isCommitDisabled = false;
      this.disableDownload = true;
    }
  }

  /**
   * @method formatDateToYMD
   * @description Format Date object to YYYY-MM-DD string format
   * @param {any} date - Date object to format
   * @returns {string} - Formatted date string (e.g., "2025-01-15")
   */
  formatDateToYMD = (date: any): string => {
    const d = new Date(date); // Convert to Date object
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  /**
   * @method addFeedLog
   * @async
   * @description Create batch transaction and transaction records with validated feed data
   *
   * Execution flow:
   * 1. Final validation of required dropdowns (business, farmsite, section, ponds)
   * 2. Lookup business name for transaction metadata
   * 3. Populate batchTransaction with metadata:
   *    - From and to dates (same date)
   *    - Batch type = 'Feed'
   *    - Farm site ID and section ID
   *    - Current date/time with proper formatting
   * 4. Build FeedLogs array from pondsDetails_dropdown:
   *    - Calculate Days of Culture (DOC) from start_date to batch entry date
   *    - Map feed quantities based on UOM (handle gram to kg conversion)
   *    - Set meal properties (meal_given, meal_consumed, last_meal flag)
   *    - Lookup feed item master data for conversion rules
   * 5. Check request object size (must be ≤ 100KB)
   * 6. Call BatchTransactionsService.addBatchFeedLogs() to create batch
   * 7. Extract source transaction IDs from response
   * 8. Update request object with source transaction IDs
   * 9. Call TransactionsService.addFeelogTransaction() to create transaction records
   * 10. Emit gotoBatchList event on success
   *
   * Error handling:
   * - Display validation errors for missing dropdowns
   * - Display error if request size exceeds 100KB
   * - Restore submit buttons on failure
   *
   * @param {any} transaction - Transaction request object with feedTransactions array
   * @returns {Promise<boolean|void>} - False if validation fails, void if success/error
   * @async
   * @throws {Error} Caught and displayed via userFriendlyAlertMessages
   * @side-effect
   * - Sets: batchTransaction properties with complete feed data
   * - Calls: BatchTransactionsService.addBatchFeedLogs() to create batch
   * - Calls: TransactionsService.addFeelogTransaction() to create transactions
   * - Emits: gotoBatchList event on successful creation
   * - Sets: isCommitDisabled=false, disableDownload=false on success
   * - Restores: editButton, submitButton states on error
   *
   * @description Data Mapping:
   * - Feed quantities are mapped based on UOM type
   * - Gram quantities are preserved (not converted to kg)
   * - Feed consumed = feed given (calculated from check tray if needed)
   * - Meal given = total feed quantity for the meal
   * - DOC = Days of Culture calculated from pond start_date
   */
  public async addFeedLog(transaction: any) {
    try {
      if (!this.paramsbusinessId) {
        this.toasterService.error('Please select farm');
        return false;
      }
      if (!this.paramsfarmsiteId) {
        this.toasterService.error('Please select farmsite');
        return false;
      }
      if (!this.paramssectionId) {
        this.toasterService.error('Please select section');
        return false;
      }
      for (let i = 0; i < this.pondsDetails_dropdown.length; i++) {
        if (!this.pondsDetails_dropdown[i].pond_id) {
          this.toasterService.error('Please select ponds');
          return false;
        }
      }
      const formattedDate = this.formatDateToYMD(this.date_time);
      let itmsArr = _.filter(this.businessEntities, (o) => {
        return o.id == this.paramsbusinessId;
      });
      if (itmsArr.length > 0) {
        this.businessName = itmsArr[0].businessName;
      }
      this.batchTransaction.fromDate = formattedDate;
      this.batchTransaction.toDate = formattedDate
      this.batchTransaction.batch_type = 'Feed';
      this.batchTransaction.farm_site_id = this.paramsfarmsiteId;
      const selectedDate = new Date(this.date_time); // existing date from datepicker
      const now = new Date(); // current time
      // Apply current hours and minutes to selected date
      selectedDate.setHours(now.getHours());
      selectedDate.setMinutes(now.getMinutes());
      // Optional: pad function to ensure 2-digit formatting
      const pad = (n: number) => String(n).padStart(2, '0');
      // Format: YYYY-MM-DD HH:mm
      const finalDate = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())} ${pad(selectedDate.getHours())}:${pad(selectedDate.getMinutes())}`;
      this.batchTransaction.date_time = finalDate;
      this.batchTransaction.section_id = Number(this.paramssectionId);
      this.batchTransaction.FeedLogs = new Array();
      let feedLogsList = [];
      let count = -1;
      for (let i = 0; i <= this.pondsDetails_dropdown.length - 1; i++) {
        let eachMydata = JSON.parse(JSON.stringify(this.pondsDetails_dropdown[i].FeedLogs));
        // this.pondsDetails_dropdown[j].FeedLogs[index].itemUom
        for (let j = 0; j <= eachMydata.length - 1; j++) {
          let eachObj = JSON.parse(JSON.stringify(eachMydata[j]));
          if (eachObj.feedMeal.feedTypes[0].feed_item_id != -1) {
            count++;
            const formatedTime = this.given_time = moment(this.given_time, ['h:mm A']).format('HH:mm');
            eachObj.pond_id = this.pondsDetails_dropdown[i].pond_id;
            eachObj.culture_category_id = this.pondsDetails_dropdown[i].culture_category_id;
            let date1 = new Date(this.pondsDetails_dropdown[i].start_date.split('T')[0]);
            let date2 = new Date(this.batchEntrtyDate);
            let Difference_In_Time = date2.getTime() - date1.getTime();
            let diffDays = Math.ceil(Difference_In_Time / (1000 * 60 * 60 * 24)) + 1;
            eachObj.doc = diffDays;
            eachObj.pond_culture_id = this.pondsDetails_dropdown[i].id;
            eachObj.present_no_of_animals = this.pondsDetails_dropdown[i].present_no_of_animals;
            eachObj.stocking_count = this.pondsDetails_dropdown[i].stocking_count;
            eachObj.start_date = this.pondsDetails_dropdown[i].start_date;
            eachObj.business_id = this.paramsbusinessId;
            eachObj.person_id = localStorage.personID;
            eachObj.name = this.pondsDetails_dropdown[i].pond_name;
            eachObj.feedMeal.pond_id = this.pondsDetails_dropdown[i].pond_id;
            eachObj.feedMeal.pond_culture_id = this.pondsDetails_dropdown[i].id;
            eachObj.feedMeal.given_time = formatedTime;
            eachObj.feedMeal.smart_meal = false;
            eachObj.feedMeal.last_meal = ((this.pondsDetails_dropdown[i].FeedLogs.length - 1) == j);
            eachObj.checkTray.feedTypes['feed_item_id'] = Number(this.pondsDetails_dropdown[i].FeedLogs[j].feedMeal.feedTypes[0].feed_item_id);
            eachObj.checkTray.check_tray_feed_given = Number(this.pondsDetails_dropdown[i].FeedLogs[j].checkTray.check_tray_feed_given);
            eachObj.checkTray.check_tray_feed_consumed = Number(this.pondsDetails_dropdown[i].FeedLogs[j].checkTray.check_tray_feed_consumed);
            eachObj.checkTray.check_tray_feed_required = 0;
            // eachObj.checkTray.check_tray_feed_consumed = null;
            eachObj.checkTray.check_tray_feed_remaining = null;
            eachObj.checkTray.feedTypes['feed_qty_given'] = Number(this.pondsDetails_dropdown[i].FeedLogs[j].checkTray.check_tray_feed_given);
            eachObj.checkTray.feedTypes['mix_item_id'] = this.pondsDetails_dropdown[i].FeedLogs[j].feedMeal.feedTypes[0].mix_item_id != -1 ? Number(this.pondsDetails_dropdown[i].FeedLogs[j].feedMeal.feedTypes[0].mix_item_id) : null;
            eachObj.feedMeal.feedTypes[0].mix_item_id = this.pondsDetails_dropdown[i].FeedLogs[j].feedMeal.feedTypes[0].mix_item_id != -1 ? Number(this.pondsDetails_dropdown[i].FeedLogs[j].feedMeal.feedTypes[0].mix_item_id) : null;
            eachObj.feedMeal.feedTypes[0].mix_qty_given = eachObj.feedMeal.feedTypes[0].mix_qty_given ? (parseFloat(eachObj.feedMeal.feedTypes[0].mix_qty_given + '')) : null;
            if (eachObj.feedMeal.feedTypes[0].feed_uom_id == 3) {
              eachObj.feedMeal.feedTypes[0].feed_qty_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '');
              /*eachObj.feed_given = (parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given) * 1000);
              eachObj.cumulative_feed = (parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given) * 1000);
              eachObj.feedMeal.meal_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '') * 1000;*/
              eachObj.feed_given = (parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given));
              eachObj.cumulative_feed = (parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given));
              eachObj.feedMeal.meal_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '');
            } else {
              // eachObj.feedMeal.feedTypes[0].feed_qty_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '') * 1000;
              eachObj.feedMeal.feedTypes[0].feed_qty_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '');
            }
            let selectedItem = this.feedTypes.find(item => item.id == eachObj.feedMeal.feedTypes[0].feed_item_id);
            eachObj.feed_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given);
            eachObj.cumulative_feed = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given);
            eachObj.feed_consumed = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given);
            eachObj.feedMeal.meal_given = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '');
            eachObj.feedMeal.meal_consumed = parseFloat(eachObj.feedMeal.feedTypes[0].feed_qty_given + '');
            eachObj.feedMeal.feedTypes[0].feed_uom_id = Number(eachObj.feedMeal.feedTypes[0].feed_uom_id);
            eachObj.feedMeal.feedTypes[0].mix_business_item_id = (eachObj.feedMeal.feedTypes[0].mix_business_item_id != '-1' && eachObj.feedMeal.feedTypes[0].mix_business_item_id != '') ? eachObj.feedMeal.feedTypes[0].mix_business_item_id : null;
            eachObj.feedMeal.feedTypes[0].mix_uom_id = Number(eachObj.feedMeal.feedTypes[0].mix_uom_id);
            eachObj.feedMeal.feedTypes[0].from_units_uom_id = Number(selectedItem.UOMConversion[0].FromUOM.id);
            eachObj.feedMeal.feedTypes[0].from_value = Number(selectedItem.UOMConversion[0].from_value);
            eachObj.feedMeal.feedTypes[0].to_value = Number(selectedItem.UOMConversion[0].to_value);
            eachObj.feedMeal.feedTypes[0].to_units_uom_id = Number(selectedItem.UOMConversion[0].ToUOM.id);
            eachObj.feedMeal.feedTypes[0].uoms = eachObj.itemUom;
            eachObj.feedMeal.feedTypes[0].uom = eachMydata[0].name;
            eachObj.feedMeal.feedTypes[0].feedUOM = eachMydata[0].name;
            eachObj.feedMeal.feedTypes[0].isManualMixUOM = eachObj.isManualMixUOM;
            eachObj.feedMeal.feedTypes[0].feedName = eachObj.feeditem_name;
            eachObj.farm_site_id = this.paramsfarmsiteId;
            if (!eachObj.feedMeal.last_meal) {
              eachObj.feedMeal.last_meal = false;
              this.noAddIconEditable = true;
            }
            feedLogsList[count] = (eachObj);
          }
        }
      }
      this.batchTransaction['FeedLogs'] = feedLogsList;
      if (this.batchTransaction) {
        const RequestObjectsize = new TextEncoder().encode(JSON.stringify(this.batchTransaction)).length;
        const RequestObjectLimit = RequestObjectsize / 1024;
        if (Math.floor(RequestObjectLimit) <= 100) {
          for (let i = 0; i < this.batchTransaction.FeedLogs.length; i++) {
            delete this.batchTransaction.FeedLogs[i].itemUom;
            delete this.batchTransaction.FeedLogs[i].mixUom;
          }
          let batchTransactions = await this.batchTransactionService.addBatchFeedLogs(this.batchTransaction);
          let sourceTransactionIds = _.uniq(batchTransactions, 'id');
          this.toasterService.success('Batch transaction created successfully', '', {
            timeOut: 3000
          });
          for (let k = 0; k <= sourceTransactionIds.length - 1; k++) {
            for (let i = 0; i <= transaction.length - 1; i++) {
              if (transaction[i]['pond_id'] == sourceTransactionIds[k]['pond_id']) {
                transaction[i]['source_transaction_id'] = sourceTransactionIds[k].id;
              }
            }
          }
          for (let i = 0; i < transaction.feedTransactions.length; i++) {
            delete transaction.feedTransactions[i].UOMConversion;
            delete transaction.feedTransactions[i].attachment;
          }
          if (transaction) {
            try {
              this.transaction = await this.transactionService.addFeelogTransaction(transaction);
              this.gotoBatchList.emit(true);
              this.isCommitDisabled = false;
              this.disableDownload = false;
            } catch (error) {
              this.commonMethodsService.userFriendlyAlertMessages(error);
              this.editButton = true;
              this.submitButton = true;
              this.disableDownload = true;
              this.isCommitDisabled = false;
            }
          }
        } else {
          this.toasterService.error('Unable to process the request due to request size is exceeded');
          this.editButton = true;
          this.submitButton = true;
          this.isCommitDisabled = false;
        }
      }
    } catch (error) {
      this.editButton = true;
      this.submitButton = true;
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method numberValidation
   * @description Validate numeric input for feed quantity fields, prevent invalid decimal/negative entries
   *
   * Validation rules:
   * 1. Allow: digits 0-9, decimal point (.), minus sign (-), Backspace
   * 2. Prevent: Letters, special characters except above
   * 3. Prevent: Dot as first character
   * 4. Prevent: Multiple dots in single number
   * 5. Prevent: Minus sign after first character (only allow leading minus)
   * 6. Prevent: Dot immediately after minus sign in first position
   * 7. Prevent: Dot at position 0 unless it's the first character
   *
   * Use case: Prevents user from entering invalid numeric values in quantity fields
   *
   * @param {any} event - Keyboard event from input field
   * @returns {void}
   * @side-effect
   * - Calls: event.preventDefault() to block invalid keystrokes
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    const cursorPosition = event.target.selectionStart;
    // Allow numbers, the dot (decimal point), the minus sign, and the Backspace key
    if (
      !/^[\d.-]$/.test(key) && // Check if the entered character is a number, dot, or minus sign
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot or a minus sign after the first character
    if ((key === '.' && value == '' && cursorPosition === 0) || (key === '.' && value.includes('.'))
    || (key === '-' && value != '' && cursorPosition != 0) || (key === '.' && value.includes('-') && cursorPosition == 1)
    || (key === '.' && value != '' && cursorPosition === 0))
    {
      event.preventDefault();
    }
  }

}
